import { DetailsList, IconButton, PrimaryButton, SelectionMode, SharedColors } from "@fluentui/react";
import * as React from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import AuthContext from "../../Services/AuthContext";
import { MoneyFormat } from "../../Utilities/MoneyFormat";
import AddVentureAgreement from "../../Panel/VentureAgreement/AddVentureAgreement";
import EditVentureAgreement from "../../Panel/VentureAgreement/EditVentureAgreement";
import FinishVentureAgreement from "../../Panel/VentureAgreement/FinishVentureAgreement";
import ImportVentureAgreement from "../../Panel/VentureAgreement/ImportVentureAgreement";
import { netGrossColumn, netGrossField, netGrossFieldName, useNetGross } from "../../Utilities/NetGrossField";

function VentureAgreement() {
    const [user] = useContext(AuthContext);
    const {actions} = useOutletContext();
    const [agreements, setAgreements] = useState([]);
    const [refresh, setRefresh] = useState(0);
    const params = useParams();
    const [summaries, setSummaries] = useState({
        'net_value': 0,
        'start_net_value': 0,
        'gross_value': 0,
        'start_gross_value': 0,
        'protocols_net_value': 0,
        'protocols_gross_value': 0,
        'left_net_value': 0,
        'left_gross_value': 0,
    });
    const [importAgreement, setImportAgreement] = useState(false);
    const [addAgreement, setAddAgreement] = useState(false);
    const [editAgreement, setEditAgreement] = useState(false);
    const [finishAgreement, setFinishAgreement] = useState(false);

    useEffect(() => {
        const abortController = new AbortController();
        async function fetchData() {
            await fetch(`/api/venture/${params.id}/agreement`, {
                method: 'GET',
                signal: abortController.signal,
                headers: {
                    'Authorization': 'Bearer ' + user.token
                }
            }).then(res => res.json())
                .then(data => {
                    let summarize = {
                        'net_value': 0,
                        'start_net_value': 0,
                        'gross_value': 0,
                        'start_gross_value': 0,
                        'protocols_net_value': 0,
                        'protocols_gross_value': 0,
                        'left_net_value': 0,
                        'left_gross_value': 0,
                    };
                    data.forEach((i) => {
                        summarize = {
                            'net_value': summarize.net_value + parseFloat(i.net_value - i.children_net_value ?? 0),
                            'start_net_value': summarize.start_net_value + parseFloat(i.net_value ?? 0),
                            'gross_value': summarize.gross_value + parseFloat(i.gross_value - i.children_gross_value ?? 0),
                            'start_gross_value': summarize.start_gross_value + parseFloat(i.gross_value ?? 0),
                            'protocols_net_value': summarize.protocols_net_value + parseFloat(i.protocols_net_value ?? 0),
                            'protocols_gross_value': summarize.protocols_gross_value + parseFloat(i.protocols_gross_value ?? 0),
                            'left_net_value': summarize.left_net_value + parseFloat(i.left_net_value ?? 0),
                            'left_gross_value': summarize.left_gross_value + parseFloat(i.left_gross_value ?? 0),
                        };
                    });
                    setAgreements(data);
                    setSummaries(summarize);
                })
                .catch(err => { console.log(err) })
        }
        fetchData();
        return () => abortController.abort();
    }, [params, refresh, user.token]);
    useEffect(() => {
        actions([
            {
                key: 'importContract',
                text: 'Importuj Koszt',
                iconProps: { iconName: 'Download' },
                onClick: () => setImportAgreement(params.id)
            },
            {
                key: 'addAgreement',
                text: 'Dodaj Koszt',
                iconProps: { iconName: 'Add' },
                onClick: () => setAddAgreement(params.id)
            }

        ]);
        return actions;
    }, []);


    const _deleteAgreement = (item) => {
        fetch(`/api/venture/agreement/${item.id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': 'Bearer ' + user.token
            }
        }).then(res => res.json())
            .then(json => { setRefresh(refresh + 1) })
            .catch(err => { console.log(err) })
    }

    const _reverseFinishAgreement = (item) => {
        fetch(`/api/venture/agreement/${item.id}/finish/reverse`, {
            method: 'PUT',
            headers: {
                'Authorization': 'Bearer ' + user.token
            }
        }).then(res => res.json())
            .then(json => { setRefresh(refresh + 1) })
            .catch(err => { console.log(err) })
    }

    const mainLink = (item) => {
        let color = SharedColors.cyanBlue10;
        let link = item.id
        let text = item.name
        if(item.parent_agreement !== null) {
            link = item.parent_agreement
            color = SharedColors.orange10;
            text = item.parent_name + ' - ' + item.name
        }

        if (item.done) {
            color = SharedColors.green10
        }

        return (
            <Link to={`${link}`}>
                <PrimaryButton styles={{root: {backgroundColor: color, borderColor: color}, rootHovered: {backgroundColor: color, borderColor: color}}} text={text} />
            </Link>
        )
    }

    const netGross = useNetGross();
    const [columns, setColumns] = useState([]);

    useEffect(() => {
        setColumns([
            {
                key: 'name',
                name: 'Nazwa',
                ariaLabel: 'Nazwa',
                fieldName: 'name',
                minWidth: 300,
                isRowHeader: true,
                isResizable: true,
                onRender: mainLink
            },
            {
                key: 'type',
                name: 'Typ',
                ariaLabel: 'Typ',
                fieldName: 'type',
                minWidth: 120,
                isResizable: true,
            },
            {
                key: 'category',
                name: 'Kategoria',
                ariaLabel: 'Kategoria',
                fieldName: 'category',
                minWidth: 160,
                isResizable: true,
            },
            {
                key: 'contractor',
                name: 'Wykonawca',
                ariaLabel: 'Wykonawca',
                fieldName: 'contractor',
                minWidth: 160,
                isResizable: true,
            },
            {
                key: 'date',
                name: 'Data',
                ariaLabel: 'Data',
                fieldName: 'date',
                minWidth: 100,
                isResizable: true,
            },
            {
                key: 'agreement_number',
                name: 'Numer Umowy',
                ariaLabel: 'Numer Umowy',
                fieldName: 'agreement_number',
                minWidth: 160,
                isResizable: true,
            },
            {
                key: 'base_value',
                name: (<>{netGrossFieldName()} (baza) <br />
                    <span className={'total'}>{MoneyFormat(summaries[netGrossField('start_')])}</span>
                </>),
                ariaLabel: 'Netto (baza)',
                fieldName: 'base_value',
                minWidth: 90,
                isResizable: true,
                onRender: (item) => (<span title={`VAT ${item.vat}%`}>{MoneyFormat(item[netGrossField()])}</span>)
            },
            {
                key: 'value',
                name: (<>{netGrossFieldName()} <br />
                    <span className={'total'}>{MoneyFormat(summaries[netGrossField()])}</span>
                </>),
                ariaLabel: 'Netto',
                fieldName: 'value',
                minWidth: 90,
                isResizable: true,
                onRender: (item) => (<span title={`VAT ${item.vat}%`}>{MoneyFormat(item[netGrossField()] - item[netGrossField('children_')])}</span>)
            },
            {
                key: 'protocols_value',
                name: (<>Protokoły {netGrossFieldName()}<br />
                    <span className={'total'}>{MoneyFormat(summaries[netGrossField('protocols_')])}</span>
                </>),
                ariaLabel: 'Protokoły',
                fieldName: 'protocols_value',
                minWidth: 90,
                isResizable: true,
                onRender: (item) => (<span title={`VAT ${item.vat}%`}>{MoneyFormat(item[netGrossField('protocols_')])}</span>)
            },
            {
                key: 'left_value',
                name: (<>Niewykonane {netGrossFieldName()}<br />
                    <span className={'total'}>{MoneyFormat(summaries[netGrossField('left_')])}</span>
                </>),
                ariaLabel: 'Niewykonane',
                fieldName: 'left_value',
                minWidth: 90,
                isResizable: true,
                onRender: (item) => (<span title={`VAT ${item.vat}%`}>{MoneyFormat(item[netGrossField('left_')])}</span>)
            },
            {
                key: 'payment_deadline',
                name: (<span style={{textWrap: 'wrap'}}>Termin Płatności</span>),
                ariaLabel: 'Termin Płatności',
                fieldName: 'payment_deadline',
                minWidth: 80,
                isResizable: true,
            },
            {
                key: 'actions',
                name: 'Akcje',
                ariaLabel: 'Akcje',
                fieldName: 'actions',
                minWidth: 110,
                onRender: (item) => (
                    <span>
                        {item.done ? (
                            <IconButton iconProps={{iconName: 'RevToggleKey'}} title={'Cofnij'} onClick={() => _reverseFinishAgreement(item)} />
                        ) : (
                            <IconButton iconProps={{iconName: 'CheckMark'}} title={'Zrealizowana'} onClick={() => setFinishAgreement(item)} />
                        )}
                            <IconButton iconProps={ {iconName: 'edit' }} title='Edytuj' onClick={() => setEditAgreement(item)}/>
                        <IconButton iconProps={ {iconName: 'delete' }} title='Usuń' onClick={() => _deleteAgreement(item)}/>
                    </span>
                )
            },
        ]);
    }, [netGross, summaries]);

    return (
        <>
            <DetailsList items={agreements} columns={columns} selectionMode={SelectionMode.none} className={'with-totals'}/>
            <AddVentureAgreement add={addAgreement} dismissPanel={() => setAddAgreement(false)} onSuccess={() => setRefresh(refresh + 1)}/>
            <EditVentureAgreement add={editAgreement} dismissPanel={() => setEditAgreement(false)} onSuccess={() => setRefresh(refresh + 1)} />
            <FinishVentureAgreement finish={finishAgreement} dismissPanel={() => setFinishAgreement(false)} onSuccess={() => setRefresh(refresh + 1)} />
            <ImportVentureAgreement add={importAgreement} dismissPanel={() => setImportAgreement(false)} onSuccess={() => setRefresh(refresh + 1)} />
        </>
    );
}

export default VentureAgreement;